/**
 * Composite "Inventory Health" score (0–100) from live desk lines.
 * Heuristic only — not clinical or financial advice.
 */

export type InventoryHealthMedInput = {
  quantity: number;
  expiryMonth: number;
  expiryYear: number;
  status?: string | null;
  pricePerBox?: number | null;
  /** When set, improves “data coverage” — supplier intelligence needs labels. */
  supplierName?: string | null;
};

export type InventoryHealthBreakdown = {
  expiryRisk: number;
  wasteExposure: number;
  dataCoverage: number;
};

export type InventoryHealthScore = {
  total: number;
  breakdown: InventoryHealthBreakdown;
  headline: string;
};

function daysUntilExpiry(m: InventoryHealthMedInput): number {
  const today = new Date();
  const last = new Date(m.expiryYear, m.expiryMonth, 0);
  return Math.ceil((last.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function isActive(m: InventoryHealthMedInput): boolean {
  return m.status !== "item_moved" && m.status !== "item_expired";
}

/**
 * @param meds Active inventory lines (caller should omit deleted / inactive buckets if desired).
 */
export function computeInventoryHealthScore(meds: InventoryHealthMedInput[]): InventoryHealthScore {
  const active = (meds || []).filter(isActive);
  if (active.length === 0) {
    return {
      total: 0,
      breakdown: { expiryRisk: 0, wasteExposure: 0, dataCoverage: 0 },
      headline: "Add stock lines to see your health score.",
    };
  }

  let urgent = 0;
  let overdue = 0;
  let priced = 0;
  let atRiskValue = 0;

  for (const m of active) {
    const d = daysUntilExpiry(m);
    if (d < 0) overdue += 1;
    else if (d <= 90) urgent += 1;
    const q = Number(m.quantity) || 0;
    const p = m.pricePerBox != null && Number.isFinite(Number(m.pricePerBox)) ? Number(m.pricePerBox) : 0;
    if (p > 0) priced += 1;
    if (d <= 90 && p > 0 && q > 0) atRiskValue += q * p;
  }

  const n = active.length;
  // Expiry risk: penalize overdue hard, then 0–90 day concentration
  const overdueRate = overdue / n;
  const urgentRate = urgent / n;
  const expiryRisk = Math.max(
    0,
    100 - overdueRate * 55 - urgentRate * 28 - Math.min(20, (overdue + urgent) * 0.5),
  );

  // Waste exposure: high unpriced stock in near window = weaker control signal
  const pricedRate = priced / n;
  const wasteExposure = Math.max(0, 100 - (1 - pricedRate) * 35 - Math.min(40, atRiskValue / 5000));

  // Data coverage: quantity, pricing, and supplier tags (feeds supplier board + future benchmarks).
  const qtyOk = active.filter((m) => (Number(m.quantity) || 0) > 0).length / n;
  const supplierRate = active.filter((m) => String(m.supplierName || "").trim()).length / n;
  const dataCoverage = Math.round(Math.min(100, 48 + qtyOk * 22 + pricedRate * 18 + supplierRate * 18));

  const total = Math.round(Math.min(100, expiryRisk * 0.45 + wasteExposure * 0.3 + dataCoverage * 0.25));

  let headline = "Solid baseline — keep tightening near-expiry lines.";
  if (total >= 80) headline = "Strong control — expiry risk is well contained.";
  else if (total >= 65) headline = "Good rhythm — a few hot spots worth a second pass.";
  else if (total >= 45) headline = "Mixed signals — prioritise overdue and 90-day window.";
  else headline = "Needs attention — overdue or near-expiry concentration is high.";

  return {
    total,
    breakdown: {
      expiryRisk: Math.round(expiryRisk),
      wasteExposure: Math.round(wasteExposure),
      dataCoverage: Math.round(dataCoverage),
    },
    headline,
  };
}

export type SupplierRiskBand = "low" | "medium" | "high";

export type SupplierRiskRow = {
  supplier: string;
  lineCount: number;
  nearExpiryLines: number;
  estimatedAtRiskZar: number;
  shareOfNearExpiryPct: number;
  /** From your own near-expiry mix only — not a cross-store index. */
  riskBand: SupplierRiskBand;
};

export function aggregateSupplierRisk(
  meds: Array<InventoryHealthMedInput & { supplierName?: string | null }>,
): SupplierRiskRow[] {
  const active = (meds || []).filter((m) => isActive(m) && String(m.supplierName || "").trim());
  const by = new Map<
    string,
    { lineCount: number; nearExpiryLines: number; estimatedAtRiskZar: number }
  >();

  let totalNear = 0;
  for (const m of active) {
    const supplier = String(m.supplierName).trim();
    const d = daysUntilExpiry(m);
    const near = d <= 90 ? 1 : 0;
    if (near) totalNear += 1;
    const q = Number(m.quantity) || 0;
    const p = m.pricePerBox != null && Number.isFinite(Number(m.pricePerBox)) ? Number(m.pricePerBox) : 0;
    const atRisk = near && p > 0 && q > 0 ? q * p : 0;
    const cur = by.get(supplier) || { lineCount: 0, nearExpiryLines: 0, estimatedAtRiskZar: 0 };
    cur.lineCount += 1;
    cur.nearExpiryLines += near;
    cur.estimatedAtRiskZar += atRisk;
    by.set(supplier, cur);
  }

  type SupplierAgg = Omit<SupplierRiskRow, "riskBand">;

  const rows: SupplierAgg[] = [...by.entries()].map(([supplier, v]) => ({
    supplier,
    lineCount: v.lineCount,
    nearExpiryLines: v.nearExpiryLines,
    estimatedAtRiskZar: Math.round(v.estimatedAtRiskZar * 100) / 100,
    shareOfNearExpiryPct:
      totalNear > 0 ? Math.round((v.nearExpiryLines / totalNear) * 1000) / 10 : v.nearExpiryLines > 0 ? 100 : 0,
  }));

  function bandFor(v: SupplierAgg, nearTotal: number): SupplierRiskBand {
    if (v.nearExpiryLines <= 0) return "low";
    if (nearTotal <= 0) return "low";
    if (v.shareOfNearExpiryPct >= 35 || v.nearExpiryLines >= 6 || v.estimatedAtRiskZar >= 15000) return "high";
    if (v.shareOfNearExpiryPct >= 18 || v.nearExpiryLines >= 3 || v.estimatedAtRiskZar >= 4000) return "medium";
    return "low";
  }

  const withBands: SupplierRiskRow[] = rows.map((r) => ({
    ...r,
    riskBand: bandFor(r, totalNear),
  }));

  return withBands.sort((a, b) => b.estimatedAtRiskZar - a.estimatedAtRiskZar || b.nearExpiryLines - a.nearExpiryLines);
}
